// graphViewport — where you were looking in each graph, persisted.
//
// Round-2 UI note: "Graphs should remember where you were looking if you close
// them - the framing".
//
// Closing an editor returns before its panel JSX (`if (!activeBP) return …`), so
// the whole ReactFlow subtree UNMOUNTS — store and all. Reopening remounts it
// with the `fitView` prop and the pan/zoom is gone. Nothing anywhere read or
// wrote a viewport: no onMove, no getViewport, no defaultViewport.
//
// The precedent that this SHOULD be sticky is already shipped: windowStore keeps
// the editor window's x/y/w/h/mode across the same close. The ReactFlow pan/zoom
// is simply the one piece nobody included.
//
// Module singleton + subscribe, mirroring shellLayout / windowStore / liveTuneStore
// — not React state, because the canvases are the writers and the editor shell
// must not re-render them to learn a viewport. Values are validated on every read
// AND write, so a corrupt entry cannot wedge a graph off-screen: the clamp IS the
// schema.
//
// SCOPE. The key is `<surface>:<containerId>`, finer than body/face/events,
// because a graph group, a state machine and an event function each mount their
// OWN ReactFlow instance with its own coordinate space — one framing cannot
// describe both, and `focusContainerId` is already plumbed through for exactly
// this identity (useCanvasFocus).
//
// The name is deliberately NOT under `aos-blueprint`: BlueprintEditor's reset
// wipes every key with that prefix, and a viewport is not blueprint data — a
// graph reset destroying your framing would be a surprise with no upside.

export const LS_KEY = 'aos-graph-viewport-v1'

// ReactFlow's own defaults. A zoom outside these renders either nothing or a
// single node's corner, and both read as "the editor broke".
const ZOOM_MIN = 0.05
const ZOOM_MAX = 4

/** The store key for one canvas. Top-level canvases have no container. */
export function viewportKey(surface, containerId) {
  return `${surface ?? ''}:${containerId ?? ''}`
}

/**
 * A viewport, or null.
 *
 * null is the load-bearing answer: "never framed" must stay distinguishable
 * from "framed at 0,0 zoom 1", or first open would skip fitView and show a
 * blank canvas with the graph somewhere off-screen.
 */
function sanitize(v) {
  if (!v || typeof v !== 'object') return null
  const { x, y, zoom } = v
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(zoom)) return null
  if (zoom <= 0) return null
  return { x, y, zoom: Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom)) }
}

let state = {}
const listeners = new Set()

function load() {
  try {
    const raw = globalThis.localStorage?.getItem(LS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    // An array (or anything else) is not this shape. Degrade to "nothing
    // saved" rather than letting a stale format throw on every editor open.
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const out = {}
    for (const [k, v] of Object.entries(parsed)) {
      const clean = sanitize(v)
      if (clean) out[k] = clean
    }
    return out
  } catch {
    return {}
  }
}

function persist() {
  try { globalThis.localStorage?.setItem(LS_KEY, JSON.stringify(state)) } catch { /* private mode */ }
}

function notify() { for (const fn of listeners) fn(state) }

state = load()

export const graphViewport = {
  /** @returns {{x,y,zoom}|null} null when this canvas has never been framed. */
  get(surface, containerId) {
    return state[viewportKey(surface, containerId)] ?? null
  },

  /** Record where a canvas is looking. Invalid input is dropped, not stored. */
  set(surface, containerId, viewport) {
    const clean = sanitize(viewport)
    if (!clean) return false
    const key = viewportKey(surface, containerId)
    const prev = state[key]
    if (prev && prev.x === clean.x && prev.y === clean.y && prev.zoom === clean.zoom) return true
    state = { ...state, [key]: clean }
    persist()
    notify()
    return true
  },

  /** Forget one canvas — e.g. its graph was reset out from under the framing. */
  clear(surface, containerId) {
    const key = viewportKey(surface, containerId)
    if (!(key in state)) return
    const next = { ...state }
    delete next[key]
    state = next
    persist()
    notify()
  },

  subscribe(fn) {
    listeners.add(fn)
    fn(state)
    return () => listeners.delete(fn)
  },

  /** Re-read from storage. Tests, and the seam a reload would otherwise be. */
  _reset() { state = load(); notify() },
}

if (typeof window !== 'undefined') window.__GRAPH_VIEWPORT__ = graphViewport
